import { Fragment, FC, useEffect, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { CommonStyle, Loader } from "Components";
import PatientApi from "Service/Patient.api";
import NoData from "Components/NoData";
import { SideModalProps } from "DataTypes/Modal";
import { PatientInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { RedoxVitalType } from "DataTypes/Services/Patient.type";
import RedoxFormatter from "Utils/Redox.Formatter";
import DateTime from "Utils/DateTime";
import ImgPath from "Constant/Imgs";
import UpdateVital from "./UpdateVital";
import CstmBtn from "Components/CstmBtn";
import useEhrSync from "Hooks/useEhrSync";
import EhrLastSync from "Components/EhrLastSync";
import useEhrRefresh from "Hooks/useEhrRefresh";
import { EhrDomains } from "Constant/Ehr";
import { label } from "Constant/En";

export interface DataActionType {
  data: RedoxVitalType | null;
  action: "ADD" | "EDIT" | "VIEW" | "DELETE" | null;
}

const initailState: DataActionType = { data: null, action: null };

const Vital: FC<SideModalProps> = (props) => {
  const { onClose } = props;
  const { patientInfo, patientOrg }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient,
  );
  const newPaitientId = patientInfo.id;
  const [vitalData, setVitalData] = useState<RedoxVitalType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataAction, setDataAction] = useState<DataActionType>(initailState);

  useEffect(() => {
    fetchVitals();
  }, []);

  const fetchVitals: () => void = async () => {
    setLoading(true);
    const res: any = await PatientApi.RedoxVitals({ id1: newPaitientId });
    if (res?.success) {
      setVitalData(RedoxFormatter.VitalList(res?.data?.vitals));
    } else setVitalData([]);
    setLoading(false);
  };

  const ehrSync = useEhrSync(EhrDomains.VITALS);
  useEhrRefresh(EhrDomains.VITALS, fetchVitals);

  const handleCloseLayer2: () => void = () => {
    setDataAction(initailState);
  };

  const Layer1Header = (
    <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
      <Typography fontWeight="bold">Vitals</Typography>
      {/* Controls on one line, with the sync stamp beneath them:
          stacking it under the button alone makes that column taller
          and drops the icons out of line with it. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          alignItems: "flex-end",
        }}
      >
        <Box sx={{ ...CommonStyle.sxRow }}>
          {ehrSync.canSync && (
            <Tooltip arrow placement="top" title={ehrSync.title}>
              <span>
                <CstmBtn
                  label={label.syncFromEhr}
                  btnType="primary"
                  loading={ehrSync.syncing}
                  loadingText="Syncing..."
                  onClick={ehrSync.handleSync}
                  sxProps={{ mr: 1, py: 0.4, minWidth: "auto", px: 1.2 }}
                />
              </span>
            </Tooltip>
          )}
          <Box
            sx={{ ...CommonStyle.sxRoundIconsMainSm }}
            onClick={() => {
              handleCloseLayer2();
              onClose();
            }}
          >
            <ImgPath.CloseIcon titleAccess="Close" />
          </Box>
        </Box>
        <EhrLastSync sync={ehrSync} />
      </Box>
    </Box>
  );

  const Layer1List = vitalData.map((el) => {
    const newVal = [el?.value, el?.unit, el?.targetSitename, el?.interpretation]
      .filter(Boolean)
      .join(" ");
    return (
      <Fragment key={el.id}>
        <Box
          sx={{
            ...CommonStyle.sxRow,
            mb: 1.5,
            alignItems: "flex-start",
            "& >h6": { mr: 3 },
          }}
        >
          <Box flex={1}>
            <Typography variant="subtitle1">
              {el.name} - {newVal}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {DateTime.ToLocalDate(el.dateTime)}
            </Typography>
          </Box>
        </Box>
      </Fragment>
    );
  });

  const Layer1 = (
    <Fragment>
      {Layer1Header}
      <Box sx={{ ...sxMain }}>
        {loading ? <Loader /> : vitalData.length ? Layer1List : <NoData />}
      </Box>
    </Fragment>
  );

  return (
    <Fragment>
      {/* The list column keeps a pixel floor so its rows stop collapsing
          into a horizontal scroll on a narrow screen, and the panel beside
          it takes whatever width is left. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          width: "25%",
          minWidth: 320,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {Layer1}
      </Box>
      <Box width="1%"></Box>
      {dataAction.action && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <UpdateVital
            dataAction={dataAction}
            onClose={handleCloseLayer2}
            refreshList={fetchVitals}
          />
        </Box>
      )}
    </Fragment>
  );
};
export default Vital;

// The header sizes to its own content rather than to a percentage of the
// modal: with the sync stamp on a second line it needs more than the 10% a
// short viewport gives it, and the overflow used to land on the list below.
const sxHeader = {
  justifyContent: "space-between",
  flexShrink: 0,
  py: 1,
  px: 2,
};

const sxMain = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  px: 2,
};
